import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import {
    Link as RouterLink,
    useSearchParams
} from 'react-router-dom';

import Swal from 'sweetalert2';

import {
    Box,
    Breadcrumbs,
    Button,
    Container,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
    useMediaQuery
} from '@mui/material';

import {
    deleteCustomerReview,
    getMyPendingReviews,
    getMyReviews,
    getMyReviewSummary,
    reviewErrorText
} from '../../../actions/review';
import { msg } from '../../../actions/swal_msg';
import ReviewModal from '../../Tienda/Product/Review/ReviewModal';
import MyReviewList from './MyReviewList';
import PendingReviewList from './PendingReviewList';
import ReviewSummaryCards from './ReviewSummaryCards';

const EMPTY_SUMMARY = {
    pending: 0,
    published: 0,
    hidden: 0
};

const EMPTY_PAGINATION = {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
};

const readPage = value => {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

const MyReviewsComponent = () => {
    const matches = useMediaQuery('(max-width: 950px)');
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get('tab') === 'escritas' ?
        'escritas'
    :
        'pendientes';
    const page = readPage(searchParams.get('page'));
    const search = searchParams.get('search') || '';
    const status = searchParams.get('status') || 'all';
    const rating = searchParams.get('rating') || '';
    const defaultSort = 'newest';
    const sort = searchParams.get('sort') || defaultSort;
    const variantId = searchParams.get('variant_id') || '';
    const reviewId = searchParams.get('review_id') || '';

    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summaryAvailable, setSummaryAvailable] = useState(false);
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState(EMPTY_PAGINATION);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchInput, setSearchInput] = useState(search);
    const [deletingId, setDeletingId] = useState('');
    const [refresh, setRefresh] = useState(0);
    const [modal, setModal] = useState({
        open: false,
        action: 'add',
        initialRating: 0,
        product: null,
        review: null,
        variant: null
    });
    const openedDeepLink = useRef('');

    const updateParams = (changes, remove = []) => {
        const next = new URLSearchParams(searchParams);

        remove.forEach(key => next.delete(key));
        Object.entries(changes).forEach(([key, value]) => {
            if (value === '' || value === null || value === undefined)
                next.delete(key);
            else
                next.set(key, value);
        });
        setSearchParams(next);
    };

    const loadSummary = useCallback(async () => {
        setSummaryLoading(true);

        try {
            const body = await getMyReviewSummary();

            if (body.status) {
                setSummary(body.resp);
                setSummaryAvailable(true);
            } else {
                setSummaryAvailable(false);
            }
        } catch (requestError) {
            setSummaryAvailable(false);
        } finally {
            setSummaryLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSummary();
    }, [loadSummary, refresh]);

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            setLoading(true);
            setError('');

            try {
                const filters = {
                    page,
                    limit: 10,
                    search,
                    sort
                };
                const body = tab === 'pendientes' ?
                    await getMyPendingReviews({
                        ...filters,
                        variant_id: variantId
                    })
                :
                    await getMyReviews({
                        ...filters,
                        status,
                        rating,
                        review_id: reviewId
                    });

                if (!mounted)
                    return;

                if (!body.status) {
                    setItems([]);
                    setPagination(EMPTY_PAGINATION);
                    setError(reviewErrorText(body.msg));
                    return;
                }

                setItems(tab === 'pendientes' ?
                    body.resp.products
                :
                    body.resp.reviews
                );
                setPagination(body.resp.pagination);
            } catch (requestError) {
                if (mounted) {
                    setItems([]);
                    setPagination(EMPTY_PAGINATION);
                    setError('No se pudieron cargar tus reseñas.');
                }
            } finally {
                if (mounted)
                    setLoading(false);
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, [
        page,
        rating,
        refresh,
        reviewId,
        search,
        sort,
        status,
        tab,
        variantId
    ]);

    useEffect(() => {
        setSearchInput(search);
    }, [search]);

    useEffect(() => {
        if (loading)
            return;

        if (tab === 'pendientes' && variantId) {
            const item = items.find(
                value => value.variant.id === variantId
            );

            if (item && openedDeepLink.current !== variantId) {
                openedDeepLink.current = variantId;
                setModal({
                    open: true,
                    action: 'add',
                    initialRating: 0,
                    product: item.product,
                    review: null,
                    variant: {
                        _id: item.variant.id,
                        variant_title: item.variant.title
                    }
                });
            }
        }

        if (tab === 'escritas' && reviewId) {
            const element = document.getElementById(`review-${reviewId}`);

            if (element)
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [items, loading, reviewId, tab, variantId]);

    const changeTab = (event, value) => {
        openedDeepLink.current = '';
        updateParams({
            tab: value,
            page: 1,
            sort: 'newest'
        }, [
            'rating',
            'review_id',
            'status',
            'variant_id'
        ]);
    };

    const submitSearch = event => {
        event.preventDefault();
        updateParams({
            search: searchInput.trim(),
            page: 1
        }, ['review_id', 'variant_id']);
    };

    const openPendingReview = (item, initialRating) => {
        setModal({
            open: true,
            action: 'add',
            initialRating,
            product: item.product,
            review: null,
            variant: {
                _id: item.variant.id,
                variant_title: item.variant.title
            }
        });
    };

    const openReviewEdit = review => {
        setModal({
            open: true,
            action: 'edit',
            initialRating: 0,
            product: review.product,
            review,
            variant: {
                _id: review.variant.id,
                variant_title: review.variant.title
            }
        });
    };

    const handleSaved = () => {
        setRefresh(value => value + 1);
    };

    const handleDelete = review => {
        Swal.fire({
            title: '¿Deseas eliminar tu reseña?',
            text: `Dejará de mostrarse y ${review.product.title} volverá a tus pendientes.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar'
        }).then(async result => {
            if (!result.isConfirmed)
                return;

            setDeletingId(review.id);

            try {
                const body = await deleteCustomerReview(review.id);

                if (!body.status) {
                    msg('warning', 'Reseña', reviewErrorText(body.msg));
                    return;
                }

                msg(
                    'success',
                    'Reseña eliminada',
                    'El producto volvió a tus pendientes por reseñar'
                );
                updateParams({ page: 1 }, ['review_id']);
                setRefresh(value => value + 1);
            } catch (requestError) {
                msg('error', 'Reseña', 'No se pudo eliminar la reseña');
            } finally {
                setDeletingId('');
            }
        });
    };

    return (
        <Container
            maxWidth="xl"
            sx={{ mt: matches ? 5 : 18 }}
            className="last-view"
        >
            <Breadcrumbs aria-label="user breadcrumb" sx={{ mb: 2 }}>
                <Link component={RouterLink} color="inherit" to="/mi-cuenta">
                    Mi cuenta
                </Link>
                <Typography color="text.primary">Mis reseñas</Typography>
            </Breadcrumbs>

            <Typography variant="h4" sx={{ fontWeight: 'light' }}>
                Mis reseñas
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
                Comparte tu experiencia con los productos que recibiste y
                administra las reseñas que ya publicaste.
            </Typography>

            <ReviewSummaryCards
                available={summaryAvailable}
                loading={summaryLoading}
                summary={summary}
            />

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                <Tabs
                    value={tab}
                    onChange={changeTab}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab
                        label={`Por reseñar${
                            summaryAvailable ? ` (${summary.pending})` : ''
                        }`}
                        value="pendientes"
                    />
                    <Tab
                        label={`Mis reseñas${
                            summaryAvailable ?
                                ` (${summary.published + summary.hidden})`
                            :
                                ''
                        }`}
                        value="escritas"
                    />
                </Tabs>
            </Box>

            <Stack
                component="form"
                onSubmit={submitSearch}
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ my: 3 }}
            >
                <TextField
                    label="Buscar producto, variante o pedido"
                    size="small"
                    value={searchInput}
                    onChange={({ target }) => setSearchInput(target.value)}
                    inputProps={{ maxLength: 100 }}
                    sx={{ flex: 1 }}
                />
                {tab === 'escritas' &&
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Visibilidad</InputLabel>
                        <Select
                            label="Visibilidad"
                            value={status}
                            onChange={({ target }) => updateParams({
                                status: target.value,
                                page: 1
                            }, ['review_id'])}
                        >
                            <MenuItem value="all">Todas</MenuItem>
                            <MenuItem value="published">Publicadas</MenuItem>
                            <MenuItem value="hidden">Ocultas</MenuItem>
                        </Select>
                    </FormControl>
                }
                {tab === 'escritas' &&
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Calificación</InputLabel>
                        <Select
                            label="Calificación"
                            value={rating}
                            onChange={({ target }) => updateParams({
                                rating: target.value,
                                page: 1
                            }, ['review_id'])}
                        >
                            <MenuItem value="">Todas</MenuItem>
                            {[5, 4, 3, 2, 1].map(value =>
                                <MenuItem value={value} key={value}>
                                    {value} estrellas
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                }
                <FormControl size="small" sx={{ minWidth: 190 }}>
                    <InputLabel>Ordenar</InputLabel>
                    <Select
                        label="Ordenar"
                        value={sort}
                        onChange={({ target }) => updateParams({
                            sort: target.value,
                            page: 1
                        })}
                    >
                        <MenuItem value="newest">Más recientes</MenuItem>
                        <MenuItem value="oldest">Más antiguas</MenuItem>
                        {tab === 'escritas' &&
                            <MenuItem value="highest">
                                Mayor calificación
                            </MenuItem>
                        }
                        {tab === 'escritas' &&
                            <MenuItem value="lowest">
                                Menor calificación
                            </MenuItem>
                        }
                    </Select>
                </FormControl>
                <Button type="submit" variant="outlined">
                    Buscar
                </Button>
            </Stack>

            {tab === 'pendientes' ?
                <PendingReviewList
                    error={error}
                    loading={loading}
                    products={items}
                    pagination={pagination}
                    onPageChange={value => updateParams({ page: value })}
                    onReview={openPendingReview}
                />
            :
                <MyReviewList
                    deletingId={deletingId}
                    error={error}
                    loading={loading}
                    reviews={items}
                    pagination={pagination}
                    onDelete={handleDelete}
                    onEdit={openReviewEdit}
                    onPageChange={value => updateParams({ page: value })}
                />
            }

            {modal.variant &&
                <ReviewModal
                    action={modal.action}
                    initialRating={modal.initialRating}
                    open={modal.open}
                    product={modal.product}
                    productId={modal.product.id}
                    review={modal.review}
                    variant={modal.variant}
                    onClose={() => setModal(current => ({
                        ...current,
                        open: false
                    }))}
                    onSaved={handleSaved}
                />
            }
        </Container>
    );
};

export default MyReviewsComponent;
